/**
 * What the room list's <b>Operators</b> column says about one room.
 *
 * <p>
 * <b>Names only, and never a role status.</b> The names are database rows; a role status is
 * Microsoft Graph's answer, and the column has to render when Graph is unreachable. It does so by
 * not asking: the read this filters degrades to a complete list with every status unknown
 * (<code>IDirectoryRoles</code> never throws), and this function never looks at that field.
 * The role belongs where somebody can act on it, which is the dialog.
 * </p>
 */

/**
 * How many names a cell shows before it starts counting.
 *
 * <p>
 * Three, because the row already carries seven columns: two reads as a stub and five wraps at the
 * widths this console is used at. Capped at EVERY width — a desktop cell listing eleven names is
 * no more readable than a phone's, and two truncations by breakpoint is a rule somebody maintains.
 * Overview's <code>GLANCE_LIMIT</code> is the same shape and the same argument.
 * </p>
 */
export const COLUMN_NAME_LIMIT = 3;

/**
 * What a link with no name reads as.
 *
 * <p>
 * The operator list's own wording, deliberately shared rather than reinvented: one person must not
 * read two ways on two screens. <code>DisplayName</code> is genuinely nullable, so this is a
 * reachable state rather than a defensive one — though nothing that goes through the API can
 * create it, since both writers refuse an empty name.
 * </p>
 */
export const UNNAMED_OPERATOR = 'Unnamed operator';

/** As much of an assignment as this column reads. Structural, so the DTO passes straight in. */
export type OperatorLink = { roomId: string; displayName?: string | null };

/**
 * The state of the one read every cell shares.
 *
 * <p>
 * <b>Three states and not a nullable list</b>, because the two absences mean different things and
 * only one of them is recoverable by waiting. Spelling them the same is how "None" ends up under a
 * room that has four operators.
 * </p>
 */
export type OperatorRead =
  | { status: 'pending' }
  | { status: 'failed' }
  | { status: 'ready'; operators: readonly OperatorLink[] };

export type OperatorsCell =
  | { kind: 'pending' }
  | { kind: 'unavailable' }
  | { kind: 'none' }
  /** <paramref name="hidden"/> counts what is NOT shown, so names.length + hidden is the total. */
  | { kind: 'names'; names: string[]; hidden: number };

const nameOf = (link: OperatorLink) => link.displayName?.trim() || UNNAMED_OPERATOR;

const named = (link: OperatorLink) => (link.displayName?.trim() ?? '').length > 0;

export const operatorsCell = (read: OperatorRead, roomId: string): OperatorsCell => {
  if (read.status !== 'ready') {
    return read.status === 'pending' ? { kind: 'pending' } : { kind: 'unavailable' };
  }

  const here = read.operators.filter((o) => o.roomId === roomId);
  if (here.length === 0) return { kind: 'none' };

  // NAMED FIRST. The server orders by (RoomId, DisplayName) and SQL Server sorts NULL first, so
  // truncating in arrival order can spend all three slots on "Unnamed operator" while every named
  // colleague hides behind the count. This decides what to SHOW and reorders nothing anybody reads
  // in full, which is what makes preferring the informative rows honest rather than an opinion.
  // Within each group the server's order is kept — it is already alphabetical, and a second sort
  // here would be a second answer that disagrees silently the day the ORDER BY changes.
  const ordered = [...here.filter(named), ...here.filter((o) => !named(o))];

  return {
    kind: 'names',
    names: ordered.slice(0, COLUMN_NAME_LIMIT).map(nameOf),
    hidden: Math.max(0, ordered.length - COLUMN_NAME_LIMIT),
  };
};

/**
 * The shared read, as the query reports it.
 *
 * <p>
 * <b>The answer wins over the failure, deliberately.</b> A background refetch that fails leaves
 * TanStack holding the previous answer and raising <code>isError</code> at the same time; reading
 * the failure first would blank the one column on the row that says who runs the room, in exchange
 * for nothing. Nothing here is a figure anybody is paid against, so stale beats absent.
 * </p>
 * <p>
 * Structural rather than typed against <code>UseQueryResult</code>, so it can be driven from a
 * table of four literals — and so the one decision in it is checkable without a query client.
 * </p>
 */
export const operatorReadOf = (query: {
  isPending: boolean;
  isError: boolean;
  data?: { data: readonly OperatorLink[] } | undefined;
}): OperatorRead => {
  if (query.data) return { status: 'ready', operators: query.data.data };

  return query.isError ? { status: 'failed' } : { status: 'pending' };
};

/**
 * What deleting this room takes with it, in operator links.
 *
 * <p>
 * <b>A figure, because the confirmation beside it gives two.</b> The dialog already states the
 * players and tournaments it destroys — an administrator deciding an irreversible thing needs the
 * size of what goes — and operator links were the one part named without a number, which reads as
 * boilerplate next to two real counts.
 * </p>
 * <p>
 * <b>And it never invents one.</b> This sentence sits inside a type-the-name gate for something
 * that cannot be undone, so a count guessed at while the read was in flight, or had failed, would
 * be the single number on that screen nobody could rely on. Unknown says the links go and stops
 * there — which is what the sentence said before it could count at all.
 * </p>
 */
export const operatorLinkNote = (read: OperatorRead, roomId: string): string => {
  if (read.status !== 'ready') return 'the operator links pointing at it';

  const count = read.operators.filter((o) => o.roomId === roomId).length;

  if (count === 0) return 'no operator links';

  return `${count} operator link${count === 1 ? '' : 's'}`;
};
